import { navigate } from "gatsby";
import _ from "lodash";
import React, { PureComponent } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { Link } from "react-scroll";
import styled, { css } from "styled-components";

import images from "src/images";
import { translate, translateLocale, convertLocale } from "src/locales";
import { colors } from "src/styles/colors";
import { GNB, GNBSub } from "../Typography";
import zIndex from "src/styles/zIndexes";
import { gTagTracking } from "src/decorators/analyticsTracking";
import { CategoryType, ActionType } from "src/configs/analytics";
import { env } from "src/configs/env";
import { getDashboardUrl } from "src/utils/uri";
import { getLinksByLanguage } from "src/constants/link";

type Props = {
  className?: string;
  isKo: boolean;
  onHomeLink: () => void;
  onProductLink: (hash: string) => void;
  onLocaleChange: () => void;
} & WithTranslation;

const Container = styled.div<{ $isKo: boolean }>`
  position: fixed;
  right: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-height: 100%;
  z-index: ${zIndex.ten};
  ${({ $isKo }) =>
    $isKo
      ? css`
          top: 106px;
        `
      : css`
          top: 54px;
        `}
`;

const InnerGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  overscroll-behavior: contain;
  padding-bottom: 102px;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 8px;
  padding-right: 0;
  padding-bottom: 16px;
  background-color: #fff;
`;

const MenuGroupItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const MenuGroupTitle = styled(GNB)`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 48px;
  padding-left: 20px;
  padding-right: 20px;
  color: ${colors.text.dark.title};
`;

const ProductMenuGroupTitle = styled(MenuGroupTitle)`
  transition: all 0.15s linear 0s;
  cursor: pointer;
  &:hover {
    background-color: ${colors.bg.white.sub};
  }
`;

const MenuItem = styled(Link)`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const DeveloperButton = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const LinkIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-left: 8px;
`;

const MenuTitle = styled(GNBSub)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 48px;
  padding-left: 40px;
  padding-right: 20px;
  color: ${colors.text.dark.body};
  transition: all 0.15s linear 0s;
  cursor: pointer;
  &:hover {
    background-color: ${colors.bg.white.sub};
  }
`;

const LocaleIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
`;

const Footer = styled.div`
  margin-top: 12px;
`;

const DashboardItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 56px;
  padding-left: 20px;
  padding-right: 20px;
`;

const DashboardButton = styled(GNB)`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #4c5359;
  cursor: pointer;
  background: #fafbfc;
  border-radius: 100px;
  padding: 4px 6px 4px 12px;
`;

const ArrowRightIcon = styled.img`
  width: 20px;
`;

const LangLink = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-family: Archivo;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 16.8px;
  color: ${colors.text.dark.body};
  text-align: center;
  transition: all 0.15s linear 0s;
  cursor: pointer;
  width: 90px;
  height: 56px;
  padding-left: 20px;
`;

class DropDownMenu extends PureComponent<Props> {
  public render() {
    const { isKo, i18n } = this.props;
    const { language } = i18n;
    const { className, onHomeLink, onProductLink, onLocaleChange, t } =
      this.props;

    return (
      <Container className={className} $isKo={isKo}>
        <InnerGroup>
          <Inner>
            <MenuGroupItem>
              <MenuItem
                key={"mobileProduct"}
                to="multisigWallet"
                smooth={true}
                spy={true}
                hashSpy={true}
                isDynamic={true}
                onClick={_.partial(onProductLink, "multisigWallet")}
              >
                <ProductMenuGroupTitle
                  onClick={_.partial(onProductLink, "multisigWallet")}
                >
                  {translate(["header", "product"], t)}
                </ProductMenuGroupTitle>
              </MenuItem>
              <MenuItem
                key={"mobileMultisigWallet"}
                activeClass="active-dropdown-menu-item"
                to="multisigWallet"
                smooth={true}
                spy={true}
                hashSpy={true}
                isDynamic={true}
                onClick={_.partial(onProductLink, "multisigWallet")}
              >
                <MenuTitle>{`- ${translate(
                  ["header", "menus", "multisigWallet"],
                  t
                )}`}</MenuTitle>
              </MenuItem>
              <MenuItem
                key={"mobileSecurity"}
                activeClass="active-dropdown-menu-item"
                to="security"
                smooth={true}
                spy={true}
                hashSpy={true}
                isDynamic={true}
                onClick={_.partial(onProductLink, "security")}
              >
                <MenuTitle>{`- ${translate(
                  ["header", "menus", "security"],
                  t
                )}`}</MenuTitle>
              </MenuItem>
              <MenuItem
                key={"mobileSaveGasFee"}
                activeClass="active-dropdown-menu-item"
                to="saveGasFee"
                smooth={true}
                spy={true}
                hashSpy={true}
                isDynamic={true}
                onClick={_.partial(onProductLink, "security")}
              >
                <MenuTitle>{`- ${translate(
                  ["header", "menus", "saveGasFee"],
                  t
                )}`}</MenuTitle>
              </MenuItem>
              <MenuItem
                key={"mobileDashboardAndAPI"}
                activeClass="active-dropdown-menu-item"
                to="dashboardAndAPI"
                smooth={true}
                spy={true}
                hashSpy={true}
                isDynamic={true}
                onClick={_.partial(onProductLink, "dashboardAndAPI")}
              >
                <MenuTitle>{`- ${translate(
                  ["header", "menus", "dashboardAndAPI"],
                  t
                )}`}</MenuTitle>
              </MenuItem>
              <MenuItem
                key={"mobileCurrencies"}
                activeClass="active-dropdown-menu-item"
                to="currencies"
                smooth={true}
                spy={true}
                hashSpy={true}
                isDynamic={true}
                onClick={_.partial(onProductLink, "currencies")}
              >
                <MenuTitle>{`- ${translate(
                  ["header", "menus", "currencies"],
                  t
                )}`}</MenuTitle>
              </MenuItem>
            </MenuGroupItem>
            <MenuGroupItem>
              <MenuGroupTitle>
                {translate(["header", "developers"], t)}
              </MenuGroupTitle>
              <DeveloperButton
                key={"mobileDocs"}
                onClick={_.partial(
                  this.navigateToLink,
                  getLinksByLanguage(language).docs
                )}
              >
                <MenuTitle>
                  {"Docs"}
                  <LinkIcon
                    src={images.externalicLinkSrc}
                    srcSet={images.externalicLink}
                  />
                </MenuTitle>
              </DeveloperButton>
              <DeveloperButton
                key={"mobileTutorials"}
                onClick={_.partial(
                  this.navigateToLink,
                  getLinksByLanguage(language).tutorials
                )}
              >
                <MenuTitle>
                  {"Tutorials"}
                  <LinkIcon
                    src={images.externalicLinkSrc}
                    srcSet={images.externalicLink}
                  />
                </MenuTitle>
              </DeveloperButton>
              <DeveloperButton
                key={"mobileApis"}
                onClick={_.partial(
                  this.navigateToLink,
                  getLinksByLanguage(language).apis
                )}
              >
                <MenuTitle>
                  {"APIs"}
                  <LinkIcon
                    src={images.externalicLinkSrc}
                    srcSet={images.externalicLink}
                  />
                </MenuTitle>
              </DeveloperButton>
            </MenuGroupItem>
            <Footer>
              <DashboardItem>
                <DashboardButton onClick={this.navigateToDashboard}>
                  {translate(["header", "dashboard"], t)}
                  <ArrowRightIcon
                    src={images.arrowRightGreySrc}
                    srcSet={images.arrowRightGrey}
                  />
                </DashboardButton>
              </DashboardItem>
              <LangLink onClick={onLocaleChange}>
                <LocaleIcon src={images.localeSrc} srcSet={images.locale} />
                {translateLocale.get(convertLocale(language))}
              </LangLink>
            </Footer>
          </Inner>
        </InnerGroup>
      </Container>
    );
  }

  @gTagTracking((__, ___, args) => {
    return {
      category: CategoryType.GNB,
      action: ActionType.HenesisWallet,
    };
  })
  private navigateToDashboard = () => {
    window.open(getDashboardUrl());
  };

  private navigateToLink = (uri: string) => {
    window.open(uri);
  };
}

export default withTranslation()(DropDownMenu);
